const GENERAL_ACCESS_MAP = {
  administracao_usuarios: ['usuarios', 'users', 'user'],
  administracao_niveis_acesso: ['niveis_acesso', 'niveis', 'roles', 'custom_roles', 'access_levels'],
  visualizar_relatorios: ['relatorios', 'relatorio', 'reports', 'report']
}

const GENERAL_ACCESS_ACTION_MAP = {
  administracao_usuarios: 'view',
  administracao_niveis_acesso: 'view',
  visualizar_relatorios: 'view'
}

const TRUE_STRING_VALUES = new Set(['1', 'true', 'yes', 'sim'])
const FALSE_STRING_VALUES = new Set(['0', 'false', 'no', 'nao', 'não', 'null', 'undefined', ''])

const toPermissionFlag = (value) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') {
    if (value === 1) return true
    if (value === 0) return false
    return !!value
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (TRUE_STRING_VALUES.has(normalized)) return true
    if (FALSE_STRING_VALUES.has(normalized)) return false
  }
  return !!value
}

const normalizePermissionValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizePermissionValue)
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = normalizePermissionValue(value[key])
      return acc
    }, {})
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (TRUE_STRING_VALUES.has(normalized)) return true
    if (FALSE_STRING_VALUES.has(normalized)) return false
  }

  if (typeof value === 'number') {
    if (value === 1) return true
    if (value === 0) return false
  }

  return value
}

export const hasCustomPermissionsPayload = (user) => {
  if (!user || typeof user !== 'object') return false
  return Object.prototype.hasOwnProperty.call(user, 'custom_permissions')
}

export const shouldUseCustomPermissions = (user) => {
  if (!hasCustomPermissionsPayload(user)) return false
  return typeof user.custom_permissions !== 'undefined'
}

export const isCustomPermissionsPayloadInvalid = (user) => !shouldUseCustomPermissions(user)

export const normalizePermissions = (permissions) => {
  if (!permissions) return {}
  if (typeof permissions === 'string') {
    try {
      const parsedPermissions = JSON.parse(permissions)
      if (!parsedPermissions || typeof parsedPermissions !== 'object') return {}
      return normalizePermissionValue(parsedPermissions)
    } catch (e) {
      return {}
    }
  }
  if (typeof permissions === 'object') return normalizePermissionValue(permissions)
  return {}
}

const hasActionInCategory = (permissions, category, action) => {
  const categoryPermissions = permissions[category]
  if (categoryPermissions === true) return true
  if (!categoryPermissions || typeof categoryPermissions !== 'object') return false

  if (action === 'view') {
    const viewAliases = ['view', 'viewAny', 'visualizar']
    const hasViewAlias = viewAliases.some(alias => typeof categoryPermissions[alias] !== 'undefined')

    if (hasViewAlias) {
      return viewAliases.some(alias => (
        typeof categoryPermissions[alias] !== 'undefined' &&
        toPermissionFlag(categoryPermissions[alias])
      ))
    }
  }

  if (typeof categoryPermissions[action] !== 'undefined') {
    return toPermissionFlag(categoryPermissions[action])
  }

  return toPermissionFlag(categoryPermissions.view)
}

export const hasGeneralAccessFromPermissions = (permissions, permissionKey) => {
  if (!permissions || typeof permissions !== 'object') return false

  const generalAccess = permissions.acessos_gerais
  if (generalAccess && typeof generalAccess === 'object' && typeof generalAccess[permissionKey] !== 'undefined') {
    return toPermissionFlag(generalAccess[permissionKey])
  }

  if (typeof permissions[permissionKey] !== 'undefined') {
    return toPermissionFlag(permissions[permissionKey])
  }

  const mappedCategories = GENERAL_ACCESS_MAP[permissionKey] || []
  const expectedAction = GENERAL_ACCESS_ACTION_MAP[permissionKey] || 'view'

  if (mappedCategories.some(category => hasActionInCategory(permissions, category, expectedAction))) {
    return true
  }

  return mappedCategories.some((category) => {
    const flatActionKey = `${category}.${expectedAction}`
    if (typeof permissions[flatActionKey] !== 'undefined') return toPermissionFlag(permissions[flatActionKey])

    if (expectedAction === 'view') {
      const flatViewAnyKey = `${category}.viewAny`
      if (typeof permissions[flatViewAnyKey] !== 'undefined') return toPermissionFlag(permissions[flatViewAnyKey])

      const flatBooleanKey = `${category}.visualizar`
      if (typeof permissions[flatBooleanKey] !== 'undefined') return toPermissionFlag(permissions[flatBooleanKey])
    }

    return false
  })
}

export const hasExplicitGeneralAccessFromPermissions = (permissions, permissionKey) => {
  if (!permissions || typeof permissions !== 'object') return false

  const generalAccess = permissions.acessos_gerais
  if (generalAccess && typeof generalAccess === 'object' && typeof generalAccess[permissionKey] !== 'undefined') {
    return toPermissionFlag(generalAccess[permissionKey])
  }

  if (typeof permissions[permissionKey] !== 'undefined') {
    return toPermissionFlag(permissions[permissionKey])
  }

  return false
}

export const hasGeneralAccess = (user, permissionKey) => {
  if (!user) return false
  const permissions = normalizePermissions(user.custom_permissions)
  return hasGeneralAccessFromPermissions(permissions, permissionKey)
}

export const hasExplicitGeneralAccess = (user, permissionKey) => {
  if (!user) return false
  const permissions = normalizePermissions(user.custom_permissions)
  return hasExplicitGeneralAccessFromPermissions(permissions, permissionKey)
}

export const hasGranularPermission = (user, category, action) => {
  if (!user) return false
  const permissions = normalizePermissions(user.custom_permissions)
  return hasActionInCategory(permissions, category, action)
}



// WEBPACK FOOTER //
// ./src/utils/access.js